import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DetailPanel = ({ entry, onClose }) => {
  const [showBeforeAfter, setShowBeforeAfter] = useState(false);

  if (!entry) {
    return (
      <div className="h-full flex items-center justify-center bg-muted/20">
        <div className="text-center">
          <Icon name="FileSearch" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">
            Wählen Sie einen Audit-Eintrag aus,<br />um Details anzuzeigen
          </p>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const badges = {
      success: { color: 'bg-success/10 text-success', icon: 'CheckCircle', label: 'Erfolgreich' },
      failed: { color: 'bg-error/10 text-error', icon: 'XCircle', label: 'Fehlgeschlagen' },
      pending: { color: 'bg-warning/10 text-warning', icon: 'Clock', label: 'Ausstehend' }
    };
    const badge = badges?.[status] || badges?.success;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge?.color}`}>
        <Icon name={badge?.icon} size={14} />
        {badge?.label}
      </span>
    );
  };

  return (
    <div className="h-full flex flex-col bg-card border-l border-border">
      {/* Header */}
      <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="Info" size={20} />
          Audit-Details
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          iconName="X"
          iconSize={16}
        >
          Schließen
        </Button>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-6">
        {/* Basic Information */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="FileText" size={16} />
            Grundinformationen
          </h3>
          <div className="bg-muted/30 rounded-lg p-3 space-y-2">
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Audit-ID:</span>
              <span className="text-sm font-mono text-foreground">{entry?.id}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Status:</span>
              {getStatusBadge(entry?.status)}
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Zeitstempel:</span>
              <div className="text-right">
                <div className="text-sm font-medium text-foreground">
                  {new Date(entry.timestamp)?.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </div>
                <div className="text-xs text-muted-foreground">
                  {new Date(entry.timestamp)?.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Information */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="User" size={16} />
            Benutzerinformationen
          </h3>
          <div className="bg-muted/30 rounded-lg p-3 space-y-2">
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Name:</span>
              <span className="text-sm font-medium text-foreground">{entry?.userName}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Benutzer-ID:</span>
              <span className="text-sm font-mono text-foreground">{entry?.userId}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Rolle:</span>
              <span className="text-sm text-foreground">{entry?.userRole}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Abteilung:</span>
              <span className="text-sm text-foreground">{entry?.department}</span>
            </div>
          </div>
        </div>

        {/* Action Details */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="Activity" size={16} />
            Aktionsdetails
          </h3>
          <div className="bg-muted/30 rounded-lg p-3 space-y-2">
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Aktion:</span>
              <span className="text-sm font-medium text-foreground">{entry?.actionLabel}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Kategorie:</span>
              <span className="text-sm text-foreground">{entry?.category}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Beschreibung:</span>
              <p className="text-sm text-foreground">{entry?.description}</p>
            </div>
          </div>
        </div>

        {/* Affected Data */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="Database" size={16} />
            Betroffene Daten
          </h3>
          <div className="bg-muted/30 rounded-lg p-3 space-y-2">
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Datensatz:</span>
              <span className="text-sm font-medium text-foreground">{entry?.affectedRecord}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Typ:</span>
              <span className="text-sm text-foreground">{entry?.recordType}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Sensibilität:</span>
              <span className="text-sm text-foreground capitalize">{entry?.sensitivityLevel}</span>
            </div>
          </div>
        </div>

        {/* Technical Details */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="Server" size={16} />
            Technische Details
          </h3>
          <div className="bg-muted/30 rounded-lg p-3 space-y-2">
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">IP-Adresse:</span>
              <span className="text-sm font-mono text-foreground">{entry?.ipAddress}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Sitzungs-ID:</span>
              <span className="text-sm font-mono text-foreground">{entry?.sessionId}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Benutzeragent:</span>
              <span className="text-sm text-foreground text-right break-all">{entry?.userAgent}</span>
            </div>
          </div>
        </div>

        {/* Change History */}
        {entry?.changes && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="GitCompare" size={16} />
                Änderungsverlauf
              </h3>
              <Button
                variant="outline"
                size="xs"
                onClick={() => setShowBeforeAfter(!showBeforeAfter)}
                iconName={showBeforeAfter ? 'EyeOff' : 'Eye'}
                iconSize={14}
              >
                {showBeforeAfter ? 'Verbergen' : 'Anzeigen'}
              </Button>
            </div>
            {showBeforeAfter && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs font-medium text-error">
                    <Icon name="Minus" size={14} />
                    Vorher
                  </div>
                  <div className="bg-error/5 border border-error/20 rounded-lg p-3">
                    <pre className="text-xs text-foreground whitespace-pre-wrap font-mono">
                      {JSON.stringify(entry?.changes?.before, null, 2)}
                    </pre>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs font-medium text-success">
                    <Icon name="Plus" size={14} />
                    Nachher
                  </div>
                  <div className="bg-success/5 border border-success/20 rounded-lg p-3">
                    <pre className="text-xs text-foreground whitespace-pre-wrap font-mono">
                      {JSON.stringify(entry?.changes?.after, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Compliance Information */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Icon name="Shield" size={16} />
            Compliance-Informationen
          </h3>
          <div className="bg-muted/30 rounded-lg p-3 space-y-2">
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">DSGVO-konform:</span>
              <span className="text-sm font-medium text-success flex items-center gap-1">
                <Icon name="CheckCircle" size={14} />
                Ja
              </span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Aufbewahrungsfrist:</span>
              <span className="text-sm text-foreground">{entry?.retentionPeriod}</span>
            </div>
            <div className="flex justify-between items-start">
              <span className="text-xs text-muted-foreground">Digitale Signatur:</span>
              <span className="text-sm font-mono text-foreground text-right break-all">{entry?.digitalSignature}</span>
            </div>
          </div>
        </div>
      </div>
      {/* Actions */}
      <div className="p-4 border-t border-border bg-card space-y-2">
        <Button
          variant="outline"
          fullWidth
          iconName="Download"
          iconPosition="left"
        >
          Eintrag exportieren
        </Button>
        <Button
          variant="outline"
          fullWidth
          iconName="Flag"
          iconPosition="left"
        >
          Zur Überprüfung markieren
        </Button>
      </div>
    </div>
  );
};

export default DetailPanel;